import React, { useEffect, useRef, useState } from "react";

import { getBreakPoint, useScreenWidth } from "oolib";

import { useGetDataLazy } from "../../utils/react-query-hooks/getData";
import { useGetAllUsersLazy } from '../../utils/react-query-hooks/users/useGetAllUsersLazy';

import { useDebounce } from "../../utils/customHooks";
import { ListingLayout } from "./ListingLayout";

import { useAppSettingsContext } from "../../contexts/appSettingsContext";
import { trackEventGA } from "../../trackers/GA/trackEventGA";


import { buildListingPageQueryForKS } from "../../utils/okf-ks-utils/buildListingPageQueryForKS";
import { defaultListingConfigForCardOrList } from "../PublishedListing/config/defaultListingConfigForCardOrList";
import { genBaseTableConfig } from "../PublishedListing/config/genBaseTableConfig";
import { useExtractInitBackendSortFromTableConfig } from "./utils/useExtractInitBackendSortFromTableConfig";

import { useLocation } from "react-router-dom";
import { queryClient } from "../..";
import { getSessionStorageDataForListingPage, getSessionStorageKeyForListingPage } from "./newUtils/sessionStorageUtils";
import { parseFilterFromSearchParams, mergeUrlFilterIntoActiveFilters } from "./newUtils/searchParamFilterUtils";
import { checkAccessToDownloadCsv } from "./utils/checkAccessToDownloadCsv";
import { useDataExport } from "./utils/useDataExport";
import { useGetQueryData } from "@/utils/react-query-hooks/general";



export const ListingRenderer = ({
  contentTypes,
  configs = {},
  presetActiveFilters = [],
  /*
  collectionId is IMPORTANT. its passed down from groupsStyle1 layout. it is used to add a unique identifier 
  to querykey for data fetching, and session storage key for remembering filters. 
  this way, we dont have random bugs like same filters getting applied to different collection pages
  */
  collectionId, 
  showCount = true,
  broadcastTotalCount, // fn that captures the totalcount for parent. used in linkormebedmodallex
  showSearchInput : _showSearchInput = true, //sometimes, we want to handle the search input UI externally and trigger keyword search config from there. In such cases, we want to disable the native SearchInput design. eg LinkOrEmbedModalLex
  searchTermInputValue: searchTermInputValueFromParent, // in some cases we allow the parent to define its own searchinput ui, and control the searchterminputvalue from there. eg LinkOrEmbedModalLex
  sectionTitle = undefined, //sometimes if we are not showing the cover, we might want a section title instead
  sectionLink = undefined,
  enableLazyLoad = true,
  ListingWrapper,
  enableContainerQuery,
  inTCI,
  builderProps,
  listingExport,
  exportRef, // optional ref the parent reads to trigger the same export from outside (eg the v2 cover's Export CSV)

  actionBarHeight,tabsPanelHeight,actionRef,isMobile,
  stickyTitleHeight,
  coverConfig,
  enableListingStyleSwitcher
}) => {
  const location = useLocation();
  const urlFilter = parseFilterFromSearchParams(location.search);

  const {
    sortConfig,
    getDataQuery = 'useGetDataLazy',
    getDataConfig = {},
    filterConfig: {
      filters,
      filterModuleOrientation
    } = {},
    listingStyle: _listingStyle = "CardsStyle",
    listingConfig: _listingConfig,
    keywordSearchConfig,
  } = configs;

  const {SELF_MANAGED_BASE_CONFIGS} = useGetQueryData('platformConfigs')

  //session storage
  const sessionStorageKey = getSessionStorageKeyForListingPage({contentTypes, collectionId});
  const sessionData = getSessionStorageDataForListingPage(sessionStorageKey);

  const [listingStyle, setListingStyle] = useState(sessionData?.listingStyle || _listingStyle)
  const isFirstMount = useRef(true)
  useEffect(() => {
    if(!isFirstMount.current){
      setListingStyle(listingStyle)
    }else{
      isFirstMount.current = false;
    }
  },[listingStyle])

  const defaultListingConfig = (
      listingStyle === "TableStyle" 
      ? genBaseTableConfig(contentTypes[0])
      : ['CardsStyle', 'ListStyle'].includes(listingStyle)
        && defaultListingConfigForCardOrList
    )

  let listingConfig = enableListingStyleSwitcher 
    //for the switcher approach listing config is not configurable for now
    ? defaultListingConfig
    : _listingConfig || defaultListingConfig || {};

  //this is messy but consolidating all the different ways of passing a redirect link to
  //just 1 property: linkConfig
  const defaultLinkTo = '/published-page/$valuePath(meta.kp_content_type)?id=$valuePath(_id)';   
  const _self_managed_link_config = SELF_MANAGED_BASE_CONFIGS?.listingConfig?.linkConfig;
  listingConfig.linkConfig = _self_managed_link_config || _listingConfig.linkConfig || _listingConfig.linkTo || _listingConfig.tableConfig?.link || (!inTCI ? defaultLinkTo : undefined)

  const { APP_SETTINGS } = useAppSettingsContext();

  const lang = APP_SETTINGS.lang;
  // const newListingConfigs = cloneDeep(listingConfig);

  // if (lang.value !== "en") {
  //   newListingConfigs.configs.title = [`translations.${lang.value}.main.title`];
  //   newListingConfigs.configs.subtitle = [
  //     `translations.${lang.value}.main.subtitle`,
  //   ];
  // }

  // listingConfig = newListingConfigs;

  /**
   * we are defaulting find query to fetch only published pieces, but
   * it can be overwritten by the findQuery prop, if one chooses to.
   */
  const findQuery = {
    kp_published_status: "published",
    ...(getDataConfig.findQuery || {}),
  };
  let { data : userData } = queryClient.getQueryState('userData')

  //local states
  const [activeFilters, setActiveFilters] = useState(
    mergeUrlFilterIntoActiveFilters(sessionData?.activeFilters || [], urlFilter)
  );

  const [searchTermInputText, setSearchTermInputText] = useState(
    sessionData?.searchTerm || ""
  );
  const [_searchTermInputValue, setSearchTermInputValue] = useState(
    sessionData?.searchTerm || ""
  );
  // in some cases we allow the parent to define its own searchinput ui, and control the searchterminputvalue from there. eg LinkOrEmbedModalLex
  const searchTermInputValue = searchTermInputValueFromParent || _searchTermInputValue;

  const [searchInUse, setSearchInUse] = useState(false);

  const [activeSort, setActiveSort] = useExtractInitBackendSortFromTableConfig({
    tableConfig: listingConfig?.tableConfig,
    enabled: listingStyle === "TableStyle",
    fallbackIfDisabled: sortConfig && sortConfig[0],
  });

  /*
  reset active filters, if the content types being rendered by this listing page changes
  (happens when you shift from one page using ListingPage component to another page using ListingPage component)
  */
  useEffect(() => {
    setActiveFilters(mergeUrlFilterIntoActiveFilters(sessionData?.activeFilters || [], urlFilter))
    setActiveSort(sortConfig?.[0] || activeSort);
  },[contentTypes, listingStyle])

  //update session storage
  useEffect(() => {
    const toSet = {
      searchTerm: searchTermInputValue || "",
      activeFilters: (activeFilters?.length > 0 && activeFilters) || [],
      listingStyle,
    };
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(toSet));
  }, [activeFilters, searchTermInputValue, listingStyle]);


  //custom hooks
  const debounce = useDebounce();
  const screenWidth = useScreenWidth();
  const isDesktop = screenWidth >= getBreakPoint("md");

  const getDataQueryLookup = {
    useGetDataLazy,
    useGetAllUsersLazy
  }

  const {
    error:contentError,
    data: contentData,
    status: contentStatus,
    fetchNextPage: contentFetchNextPage,
    hasNextPage,
    isFetchingNextPage: contentIsFetchingNextPage,
  } = getDataQueryLookup[getDataQuery](
    {
      activeFilters: [...presetActiveFilters, ...activeFilters], //presetActiveFilters are injected here, so that, even if filterModule 'clears' activeFilters, the presetFilters aren't cleared
      contentTypes,
      /**
       * this is not very dev friendly for now but,
       * the fallback true basically ensures on the backend 
       * that, searchterm or not, elastic search will always be
       * used as the data source. not mongodb 
       */
      searchTerm: searchTermInputValue, // || true,
      activeSort: activeSort && activeSort.sort,
      ...getDataConfig,
      findQuery,
      collectionId,
      // enabled: keywordSearchConfig ? false : true,
      ksConfig:
        searchTermInputValue &&
        buildListingPageQueryForKS({
          keyword: searchTermInputValue,
          config: { fields: keywordSearchConfig.fields },
          activeSort: activeSort?.sort,
        }),
      /**
       * collectionId is only relevant in order to give a unique query key (react query)
       * to the related content that might be fetched against a given collection page
       */
    },
    {
      onSuccess: () => {},
      onError: (err) => {},
    }
  );


  //data export hooks --------



  const { getDataToBeExported } = useDataExport({
    activeFilters,
    presetActiveFilters,
    contentTypes,
    getDataConfig,
    findQuery,
    searchTermInputValue
  });

  
  const gatedGetDataToBeExported = (listingExport && !isMobile) && checkAccessToDownloadCsv({ resourceType: contentTypes[0] }) ? getDataToBeExported : undefined;
  if (exportRef) exportRef.current = gatedGetDataToBeExported;

  const totalCount = contentData?.pages[0]?.count
  useEffect(() => {
    if(totalCount !== undefined && broadcastTotalCount){
      broadcastTotalCount(totalCount)
    }
  },[totalCount])

  const handleSearch = (id, value) => {
    setSearchTermInputText(value);
    debounce(() => {
      trackEventGA("Search Flow","Listing Searched")
      setSearchTermInputValue(value)
    });
  };

  const showSortDropdown = sortConfig && listingStyle !== "TableStyle";
  const showSearchInput = _showSearchInput && !!keywordSearchConfig === true && !keywordSearchConfig.disabled
  
  return (
    <ListingLayout
      //layouting. most important
      actionsPanelStickyTop={(stickyTitleHeight ?? 0) + (tabsPanelHeight ?? 0)}
      ONLY_DESKTOP_filtersSidebarStickyTop={(actionBarHeight ?? 0) + (tabsPanelHeight ?? 0)}
      stickyTopToViewportTopGap={0}

      // Filter props
      totalCount={totalCount}
      setActiveFilters={setActiveFilters}
      activeFilters={activeFilters}
      presetActiveFilters={presetActiveFilters}
      filterConfig={filters}
      contentTypes={contentTypes}
      
      // ListingModule props
      listingStyle={listingStyle}
      contentError={contentError}
      contentStatus={contentStatus}
      contentData={contentData}
      emptyPageConfigSearch={searchTermInputValue && searchTermInputValue !== "" ? {
        emptyStatePreset: "searchListing",
        contentTypes: contentTypes,
        searchTerm: _searchTermInputValue,
        onClick: () => {
          setSearchTermInputText("");
          debounce(() => setSearchTermInputValue(""));
        },
      } : undefined}
      sectionTitle={sectionTitle}
      sectionLink={sectionLink}
      showCount={showCount}
      listingConfig={listingConfig}
      fetchNextPage={enableLazyLoad ? contentFetchNextPage : () => {}}
      contentIsFetchingNextPage={contentIsFetchingNextPage}
      hasNextPage={hasNextPage}
      ListingWrapper={ListingWrapper}
      enableContainerQuery={enableContainerQuery}
      inTCI={inTCI}
      builderProps={builderProps}
      getDataToBeExported={gatedGetDataToBeExported}
      
      // Actions panel props
      actionRef={actionRef} // if passed can be used to calculate the actionbar height else just pass the actionbar height yourself
      isMobile={isMobile}
      coverConfigExists={!!coverConfig}
      
      // Sort related props
      showSortDropdown={showSortDropdown}
      activeSort={activeSort}
      setActiveSort={setActiveSort}
      sortConfig={sortConfig}
      
      // Search related props
      showSearchInput={showSearchInput}
      searchTermInputText={searchTermInputText}
      handleSearch={handleSearch}
      
      // Listing style switcher related
      enableListingStyleSwitcher={enableListingStyleSwitcher}
      setListingStyle={setListingStyle}
      collectionId={collectionId}
    />
  );
};

